// React imports
import { useState, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';

// External imports
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import _ from 'lodash';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

// MUI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import CircularProgress from '@mui/material/CircularProgress';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { grey, purple, blue, red, green, amber } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHandWave, faSquare, faSquareCheck, faPlus, faTable, faPenToSquare } from '@fortawesome/pro-light-svg-icons';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { faWpforms } from '@fortawesome/free-brands-svg-icons';

// Local imports
import Header from '../../Header.js';
import HomeKPIs from './HomeKPIs.js';
import NewTaskDialog from '../accounts/NewTaskDialog.js';
import OpportunitiesFilterOwner from '../components/OpportunitiesFilter.owner.js';

import isoWeek from 'dayjs/plugin/isoWeek';
dayjs.extend(weekday);
dayjs.extend(isoWeek);

function DashboardHome() {
    const { org, me, accounts, setAccounts, accesstoken, featureToggle, opportunities, reportConfiguration, exchangeRates, onUpdateOpportunity } = useOutletContext();
    const navigate = useNavigate();
    const theme = useTheme();

    const activities = useMemo(() => {
        return _.sortBy(accounts?.map(ac => (ac.reminders || [])
            .filter(r => r != null)
            .map(r => ({...r, ac: ac.name, acId: ac.id})))
            .flat()
            .filter(r => r != null),
            r => new Date(r.dueDate).getTime());
    }, [accounts]);

    if (!accounts) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    function onOpen({ account }) {
        if (account) {
            navigate(`./accounts/${account}/details`);
        }
    }

    function onNewOpportunity({ type }) {
        const id = uuidv4();
        onUpdateOpportunity({ opportunity: { status: 'draft', id, type, account: {}, offers: [], products: [], terms: [], created: new Date().getTime(), createdBy: me.email, owner: me.email }});
        navigate(`/organization/${org.alias}/opportunities/${id}/`);
    }

    return (
        <Box sx={{ paddingBottom: '36px', background: theme.palette.background.default }}>
            <Header
                icon={faHandWave}
                title={`Hi${me.firstName ? ` ${me.firstName}` : ''}!`}
                subtitle={<span>&nbsp;&nbsp;Welcome back to Monada, here&apos;s everything you need to know about your business.</span>}
                buttons={[{
                    title: 'New Opportunity',
                    icon: faPlus,
                    color: 'monada-button',
                    submenu: [
                        {
                            icon: faPenToSquare,
                            title: 'Quick Quote (Text)',
                            action: () => onNewOpportunity({ type: 'one-off-free-text' }),
                        },
                        {
                            icon: faWpforms,
                            title: 'Form Builder',
                            action: () => onNewOpportunity({ type: 'one-off' }),
                        },
                        {
                            icon: faTable,
                            title: 'Contract',
                            action: () => onNewOpportunity({ type: 'contract' }),
                        },
                    ]
                }]}
            />
            <HomeKPIs 
                org={org}
                me={me} 
                navigate={navigate} 
                accounts={accounts} 
                opportunities={opportunities} 
                reportConfiguration={reportConfiguration} 
                exchangeRates={exchangeRates}
            />
            <Calendar activities={activities} onOpen={onOpen} org={org} me={me} accounts={accounts} setAccounts={setAccounts} accesstoken={accesstoken} />
        </Box>
    );
}

export default DashboardHome;

function Calendar(props) {
    const { activities, onOpen, org, me, accounts, setAccounts, accesstoken } = props;
    const [showNewTask, setShowNewTask] = useState(false);
    const [calOwnerFilter, setCalOwnerFilter] = useState('me');
    const [showOpen, setShowOpen] = useState(true);
    const [showOverdue, setShowOverdue] = useState(true);
    const [showCompleted, setShowCompleted] = useState(false);
    const [calView, _setCalView] = useState(sessionStorage.getItem('calendar.view') || 'week');
    const setCalView = (v) => { _setCalView(v); sessionStorage.setItem('calendar.view', v); };
    const [refDate, _setRefDate] = useState(() => {
        const saved = sessionStorage.getItem('calendar.refDate');
        return saved ? dayjs(saved) : dayjs();
    });
    const setRefDate = (v) => {
        const resolved = typeof v === 'function' ? v(refDate) : v;
        _setRefDate(resolved);
        sessionStorage.setItem('calendar.refDate', resolved.toISOString());
    };
    const theme = useTheme();

    const filteredActivities = useMemo(() => {
        let list = activities;
        // Owner filter
        if (calOwnerFilter === 'me') list = list.filter(a => a.assignee === me.email || a.createdBy === me.email);
        else if (calOwnerFilter !== 'all') {
            const members = calOwnerFilter.split(',');
            list = list.filter(a => members.includes(a.assignee) || members.includes(a.createdBy));
        }
        // Status filter
        list = list.filter(a => {
            const isDone = a.status === 'completed' || a.status === 'rejected';
            const isOverdue = a.status === 'open' && a.dueDate && new Date(a.dueDate) < new Date(new Date().toDateString());
            const isOpen = a.status === 'open' && !isOverdue;
            if (isDone) return showCompleted;
            if (isOverdue) return showOverdue;
            if (isOpen) return showOpen;
            return showOpen;
        });
        return list;
    }, [activities, calOwnerFilter, me.email, showOpen, showOverdue, showCompleted]);

    const navigate = (dir) => {
        setRefDate(r => r.add(dir, calView === 'day' ? 'day' : calView === 'week' ? 'week' : 'month'));
    };

    async function onToggleTask(task) {
        if (!accounts || !accesstoken) return;
        const account = accounts.find(a => a.id === task.acId);
        if (!account) return;
        const newReminders = (account.reminders || []).map(r =>
            r.id === task.id ? { ...r, status: r.status === 'open' ? 'completed' : 'open' } : r
        );
        // Update local state immediately
        const updatedAccount = { ...account, reminders: newReminders };
        if (setAccounts) setAccounts(prev => prev.map(a => a.id === account.id ? updatedAccount : a));
        // Persist to server
        try {
            const _account = { ...updatedAccount };
            _account.updates = _account.updates?.map(u => !u.pendingDelete ? u : { ..._.omit(u, 'pendingDelete'), deleted: true });
            _account.updates = _account.updates?.map(u => !u.editHtml ? u : { ..._.omit(u, 'editHtml', 'editTags'), html: u.editHtml, tags: u.editTags });
            _account.updates = _account.updates?.map(u => _.omit(u, 'wasEdited'));
            await axios.put(`/api/accounts/${account.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, { account: _.omit(_account, ['created', 'modified', 'log']) });
        } catch (e) { console.error('Failed to toggle task', e); }
    }

    // Compute days to display based on view
    const days = useMemo(() => {
        if (calView === 'day') return [refDate];
        if (calView === 'week') {
            const start = refDate.startOf('isoWeek');
            return _.range(7).map(i => start.add(i, 'day'));
        }
        // month
        const start = refDate.startOf('month');
        const end = refDate.endOf('month');
        const firstDay = start.startOf('isoWeek');
        const totalDays = end.diff(firstDay, 'day') + 1;
        const weeks = Math.ceil(totalDays / 7);
        return _.range(weeks * 7).map(i => firstDay.add(i, 'day'));
    }, [refDate, calView]);

    const headerLabel = calView === 'day' ? refDate.format('dddd, MMMM D, YYYY')
        : calView === 'week' ? `${days[0].format('MMM D')} – ${days[6].format('MMM D, YYYY')}`
        : refDate.format('MMMM YYYY');

    const isToday = (d) => d.isSame(dayjs(), 'day');
    const isCurrentMonth = (d) => d.month() === refDate.month();

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '24px' }}>
            <Paper sx={{ padding: '24px', margin: '0px 24px', flex: 1, background: theme.palette.panel1, border: `1px solid ${theme.palette.border1}` }} elevation={0}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, pb: 2, borderBottom: `1px solid ${theme.palette.border1}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>Team Calendar</Typography>
                        <Box sx={{ maxWidth: '180px' }}>
                            <OpportunitiesFilterOwner
                                org={org}
                                me={me}
                                filter={{ members: calOwnerFilter === 'me' ? [me.email] : calOwnerFilter === 'all' ? [] : calOwnerFilter.split(',') }}
                                setFilter={(f) => {
                                    if (!f.members || f.members.length === 0) setCalOwnerFilter('all');
                                    else if (f.members.length === 1 && f.members[0] === me.email) setCalOwnerFilter('me');
                                    else setCalOwnerFilter(f.members.join(','));
                                }}
                                noMarginRight
                                mineText='My Tasks'
                            />
                        </Box>
                    </Box>
                    <ToggleButtonGroup
                        size='small'
                        value={calView}
                        exclusive
                        onChange={(e, v) => { if (v) setCalView(v); }}
                        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '11px', padding: '3px 10px' } }}
                    >
                        <ToggleButton value='day'>Day</ToggleButton>
                        <ToggleButton value='week'>Week</ToggleButton>
                        <ToggleButton value='month'>Month</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                {/* Navigation */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Button size='small' variant='outlined' disabled={isToday(refDate)} onClick={() => setRefDate(dayjs())} sx={{ textTransform: 'none', mr: 1 }}>Today</Button>
                    <IconButton size='small' onClick={() => navigate(-1)}><FontAwesomeIcon icon={faChevronLeft} /></IconButton>
                    <IconButton size='small' onClick={() => navigate(1)}><FontAwesomeIcon icon={faChevronRight} /></IconButton>
                    <Typography sx={{ fontWeight: 700, fontSize: '15px', ml: 1, flex: 1 }}>{headerLabel}</Typography>
                    <Button size='small' onClick={() => setShowNewTask(true)} sx={{ textTransform: 'none', fontSize: '11px', minWidth: 0 }}>
                        + New Task
                    </Button>
                    <Typography sx={{ fontSize: '11px', color: '#e2e8f0', mx: 0.5 }}>|</Typography>
                    <Tooltip title='Tasks that are open and not yet past their due date' arrow placement='bottom'>
                        <Typography
                            sx={{ fontSize: '11px', color: showOpen ? '#3b82f6' : '#94a3b8', cursor: 'pointer', fontWeight: showOpen ? 600 : 400, '&:hover': { color: '#3b82f6' } }}
                            onClick={() => setShowOpen(!showOpen)}
                        >
                            Open
                        </Typography>
                    </Tooltip>
                    <Typography sx={{ fontSize: '11px', color: '#e2e8f0', mx: 0.3 }}>·</Typography>
                    <Tooltip title='Tasks that are still open but past their due date' arrow placement='bottom'>
                        <Typography
                            sx={{ fontSize: '11px', color: showOverdue ? '#ef4444' : '#94a3b8', cursor: 'pointer', fontWeight: showOverdue ? 600 : 400, '&:hover': { color: '#ef4444' } }}
                            onClick={() => setShowOverdue(!showOverdue)}
                        >
                            Overdue
                        </Typography>
                    </Tooltip>
                    <Typography sx={{ fontSize: '11px', color: '#e2e8f0', mx: 0.3 }}>·</Typography>
                    <Tooltip title='Tasks that have been marked as done' arrow placement='bottom'>
                        <Typography
                            sx={{ fontSize: '11px', color: showCompleted ? '#64748b' : '#94a3b8', cursor: 'pointer', fontWeight: showCompleted ? 600 : 400, '&:hover': { color: '#64748b' } }}
                            onClick={() => setShowCompleted(!showCompleted)}
                        >
                            Completed
                        </Typography>
                    </Tooltip>
                </Box>
                {/* Calendar grid */}
                {calView === 'month' ? (
                    <TableContainer sx={{ border: `1px solid ${theme.palette.border1}`, borderRadius: '8px' }}>
                        <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
                            <TableHead>
                                <TableRow>
                                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                                        <TableCell key={d} sx={{ textAlign: 'center', fontWeight: 700, fontSize: '11px', py: 1, background: theme.palette.panel1 }}>{d}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {_.chunk(days, 7).map((week, wi) => (
                                    <TableRow key={wi}>
                                        {week.map(day => {
                                            const dayTasks = filteredActivities.filter(a => dayjs(a.dueDate).isSame(day, 'day'));
                                            return (
                                                <TableCell key={day.valueOf()} sx={{ border: `1px solid ${theme.palette.border1}`, p: 0, verticalAlign: 'top' }}>
                                                    <Box sx={{ height: '120px', p: '4px', opacity: isCurrentMonth(day) ? 1 : 0.4, background: isToday(day) ? purple[50] : 'transparent', overflow: 'hidden' }}>
                                                        <Typography sx={{ textAlign: 'right', fontSize: '11px', fontWeight: isToday(day) ? 700 : 400, color: isToday(day) ? purple[700] : 'inherit', mb: 0.5 }}>{day.date()}</Typography>
                                                        <Box sx={{ overflowY: 'auto', maxHeight: '90px' }}>
                                                            {dayTasks.map(a => (
                                                                <CalendarTask key={a.id} task={a} onOpen={onOpen} onToggle={onToggleTask} compact />
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : calView === 'week' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* Mon-Fri */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {days.slice(0, 5).map(day => {
                                const dayTasks = filteredActivities.filter(a => dayjs(a.dueDate).isSame(day, 'day'));
                                return (
                                    <Box key={day.valueOf()} sx={{ flex: '1 1 0', minWidth: 0, border: `1px solid ${theme.palette.border1}`, borderRadius: '8px', background: isToday(day) ? purple[50] : 'transparent', p: 1, overflow: 'hidden' }}>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 700, textAlign: 'center', mb: 1, color: isToday(day) ? purple[700] : 'inherit' }}>
                                            {day.format('ddd')} · {day.format('MMM D')}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: '250px', overflowY: 'auto' }}>
                                            {dayTasks.map(a => (
                                                <CalendarTask key={a.id} task={a} onOpen={onOpen} onToggle={onToggleTask} />
                                            ))}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                        {/* Sat-Sun */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {days.slice(5, 7).map(day => {
                                const dayTasks = filteredActivities.filter(a => dayjs(a.dueDate).isSame(day, 'day'));
                                return (
                                    <Box key={day.valueOf()} sx={{ flex: '1 1 0', minWidth: 0, maxWidth: 'calc(50% - 4px)', border: `1px solid ${theme.palette.border1}`, borderRadius: '8px', background: isToday(day) ? purple[50] : 'transparent', p: 1, overflow: 'hidden', opacity: 0.7 }}>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 700, textAlign: 'center', mb: 1, color: isToday(day) ? purple[700] : 'inherit' }}>
                                            {day.format('ddd')} · {day.format('MMM D')}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: '150px', overflowY: 'auto' }}>
                                            {dayTasks.map(a => (
                                                <CalendarTask key={a.id} task={a} onOpen={onOpen} onToggle={onToggleTask} />
                                            ))}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                ) : (
                    /* Day view */
                    <Box sx={{ border: `1px solid ${theme.palette.border1}`, borderRadius: '8px', p: 2, minHeight: '300px' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, mb: 2, color: isToday(refDate) ? purple[700] : 'inherit' }}>
                            {refDate.format('dddd, MMMM D')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {filteredActivities.filter(a => dayjs(a.dueDate).isSame(refDate, 'day')).map(a => (
                                <CalendarTask key={a.id} task={a} onOpen={onOpen} onToggle={onToggleTask} showTime />
                            ))}
                            {filteredActivities.filter(a => dayjs(a.dueDate).isSame(refDate, 'day')).length === 0 && (
                                <Typography sx={{ fontSize: '12px', color: '#94a3b8', py: 2, textAlign: 'center' }}>No tasks for this day</Typography>
                            )}
                        </Box>
                    </Box>
                )}
                <NewTaskDialog open={showNewTask} onClose={() => setShowNewTask(false)} accounts={accounts} org={org} me={me} accesstoken={accesstoken} setAccounts={setAccounts} />
            </Paper>
        </Box>
    );
}

function CalendarTask({ task, onOpen, onToggle, compact, showTime }) {
    const isDone = task.status === 'completed' || task.status === 'rejected';
    const isOverdue = task.status === 'open' && task.dueDate && new Date(task.dueDate) < new Date();

    const taskColor = isOverdue ? red[500] : isDone ? grey[400] : '#3b82f6';
    const bgColor = isOverdue ? red[50] : isDone ? grey[50] : '#eff6ff';

    if (compact) {
        return (
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3, py: '2px', px: '4px',
                borderRadius: '4px', borderLeft: `3px solid ${taskColor}`, backgroundColor: bgColor,
                cursor: 'pointer', '&:hover': { backgroundColor: isOverdue ? red[100] : isDone ? grey[100] : '#dbeafe' },
            }}>
                <Checkbox
                    size='small'
                    checked={isDone}
                    onChange={(e) => { e.stopPropagation(); onToggle(task); }}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ p: 0, color: taskColor, '&.Mui-checked': { color: grey[400] } }}
                />
                <Box onClick={() => onOpen({ account: task.acId })} sx={{ overflow: 'hidden', minWidth: 0, flex: 1 }}>
                    <Typography sx={{
                        fontSize: '9px', fontWeight: 600, lineHeight: 1.3,
                        textDecoration: isDone ? 'line-through' : 'none',
                        color: isDone ? grey[400] : '#334155',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                        {task.description}
                    </Typography>
                    <Typography sx={{ fontSize: '8px', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                        <span style={{ color: '#3b82f6', cursor: 'pointer' }}>{task.ac}</span>{task.assignee ? ` · ${task.assignee.split('@')[0]}` : ''}
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{
            display: 'flex', alignItems: 'flex-start', gap: 0.5, py: '4px', px: '6px',
            borderRadius: '6px', borderLeft: `3px solid ${taskColor}`, backgroundColor: bgColor,
            '&:hover': { backgroundColor: isOverdue ? red[100] : isDone ? grey[100] : '#dbeafe' },
        }}>
            <Checkbox
                size='small'
                checked={isDone}
                onChange={(e) => { e.stopPropagation(); onToggle(task); }}
                onClick={(e) => e.stopPropagation()}
                sx={{ p: 0.3, mt: '1px', color: taskColor, '&.Mui-checked': { color: grey[400] } }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                    fontSize: '11px', fontWeight: 500, lineHeight: 1.3,
                    textDecoration: isDone ? 'line-through' : 'none',
                    color: isDone ? grey[400] : '#334155',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    {task.description}
                </Typography>
                <Typography sx={{ fontSize: '9px', color: isOverdue ? red[600] : '#94a3b8' }}>
                    {dayjs(task.dueDate).format('h:mm A')} · <span onClick={() => onOpen({ account: task.acId })} style={{ color: '#3b82f6', cursor: 'pointer' }}>{task.ac}</span>{task.assignee ? ` → ${task.assignee.split('@')[0]}` : ''}
                </Typography>
            </Box>
        </Box>
    );
}